import { computed, onMounted, onUnmounted } from 'vue'
import type { Ref } from 'vue'

interface SidebarContextType {
  isExpanded: Ref<boolean>
  isMobileOpen: Ref<boolean>
  isHovered: Ref<boolean>
  hideSidebarOnDesktop: Ref<boolean>
  activeItem: Ref<string | null>
  openSubmenu: Ref<string | null>
  toggleSidebar: () => void
  toggleMobileSidebar: () => void
  setIsHovered: (isHovered: boolean) => void
  setActiveItem: (item: string | null) => void
  toggleSubmenu: (item: string) => void
  setHideSidebarOnDesktop: (value: boolean) => void
}

export function useSidebar (): SidebarContextType {
  // Use Nuxt's useState for global state
  const isExpanded = useState('sidebar-isExpanded', () => true)
  const isMobileOpen = useState('sidebar-isMobileOpen', () => false)
  const isMobile = useState('sidebar-isMobile', () => false)
  const isHovered = useState('sidebar-isHovered', () => false)
  const hideSidebarOnDesktop = useState('sidebar-hideSidebarOnDesktop', () => false)
  const activeItem = useState<string | null>('sidebar-activeItem', () => null)
  const openSubmenu = useState<string | null>('sidebar-openSubmenu', () => null)

  const handleResize = () => {
    const mobile = window.innerWidth < 768
    isMobile.value = mobile
    if (!mobile) {
      isMobileOpen.value = false
    }
  }

  onMounted(() => {
    handleResize()
    window.addEventListener('resize', handleResize)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', handleResize)
  })

  const toggleSidebar = () => {
    if (isMobile.value) {
      isMobileOpen.value = !isMobileOpen.value
    } else {
      isExpanded.value = !isExpanded.value
    }
  }

  const toggleMobileSidebar = () => {
    isMobileOpen.value = !isMobileOpen.value
  }

  const setIsHovered = (value: boolean) => {
    isHovered.value = value
  }

  const setActiveItem = (item: string | null) => {
    activeItem.value = item
  }

  const toggleSubmenu = (item: string) => {
    openSubmenu.value = openSubmenu.value === item ? null : item
  }

  const setHideSidebarOnDesktop = (value: boolean) => {
    hideSidebarOnDesktop.value = value
    if(!value){
      isMobileOpen.value = false
    }
  }

  return {
    isExpanded: computed(() => (isMobile.value || hideSidebarOnDesktop.value ? false : isExpanded.value)),
    isMobileOpen,
    isHovered,
    hideSidebarOnDesktop,
    activeItem,
    openSubmenu,
    toggleSidebar,
    toggleMobileSidebar,
    setIsHovered,
    setActiveItem,
    toggleSubmenu,
    setHideSidebarOnDesktop
  }
}
